import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import {
    calculateUserPresence,
    calculateInternalUserPresence,
} from '@dxp/core/helpers';
import {
    IContactDirectory,
    IContacts,
    IContact,
    IContactInternals,
    IContactInternal,
} from '@dxp/core/models';
import { environment } from '@dxp/core/environment';
import { LOGGER_REQUEST_NAME } from '@dxp/core/types';

interface IContactsQueryOptions {
    top: number;
    skip: number;
    orderBy: {
        propertyName: string;
        ascending: boolean;
    };
    filters: {
        propertyName: string;
        operator: string;
        value: string;
    }[];
    hasName: boolean;
    hasEmail: boolean;
    hasPhoneNumber: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class ContactsApiService {
    readonly #http = inject(HttpClient);

    getContactDirectories(): Observable<IContactDirectory[]> {
        return this.#http.get<IContactDirectory[]>(
            `${environment.rbacUrl}/contactdirectories`,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Contact Service (Get contact directories)',
                ),
            },
        );
    }

    getContactsByDirectory(
        directory: number,
        orderBy: string,
        skip: number,
        top: number,
        filters: {
            requiresName?: boolean;
            requiresEmail?: boolean;
            requiresPhone?: boolean;
        },
        searchQuery?: string,
    ): Observable<IContacts> {
        const [property, direction] = orderBy.split(' ');

        const options: IContactsQueryOptions = {
            top: top > 0 ? top : 50,
            skip: skip > 0 ? skip : 0,
            orderBy: {
                propertyName: property,
                ascending: direction ? direction === 'asc' : true,
            },
            hasName: filters.requiresName ?? false,
            hasPhoneNumber: filters.requiresPhone ?? false,
            hasEmail: filters.requiresEmail ?? false,
            filters: [],
        };

        if (searchQuery) {
            options.filters.push({
                propertyName: 'fullName',
                operator: 'Contains',
                value: searchQuery,
            });

            options.filters.push({
                propertyName: 'phoneNumber',
                operator: 'Contains',
                value: searchQuery,
            });

            options.filters.push({
                propertyName: 'emailAddress',
                operator: 'Contains',
                value: searchQuery,
            });

            options.filters.push({
                propertyName: 'customFields.value',
                operator: 'Contains',
                value: searchQuery,
            });

            options.filters.push({
                propertyName: 'contactDirectory.name',
                operator: 'Contains',
                value: searchQuery,
            });
        }

        return this.#http.post<IContacts>(
            `${environment.rbacUrl}/contactdirectories/${directory}/contacts/query`,
            options,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Contact Service (Get contacts by directories)',
                ),
            },
        );
    }

    updateContact(contact: IContact): Observable<void> {
        return this.#http.put<void>(
            `${environment.rbacUrl}/contactdirectories/${contact.directory}/contacts/${contact.id}`,
            contact,
            {
                context: new HttpContext().set(
                    LOGGER_REQUEST_NAME,
                    'Contact Service (Update contact)',
                ),
            },
        );
    }

    getInternalContacts(
        top: number,
        skip: number,
        searchQuery?: string,
    ): Observable<IContactInternals> {
        const page = Math.floor(skip / top) + 1;

        return this.#http
            .get<{
                paginationMeta: { totalCount: number };
                users: IContactInternal[];
            }>(
                `${environment.voiceUrl}/directory/${page}/${top}/false${searchQuery ? `/${searchQuery}` : ''}`,
                {
                    context: new HttpContext().set(
                        LOGGER_REQUEST_NAME,
                        'Contact Service (Get internal contacts)',
                    ),
                },
            )
            .pipe(
                map(data => ({
                    count: data.paginationMeta.totalCount,
                    users: data.users.map(user => ({
                        ...user,
                        presence: calculateUserPresence(
                            calculateInternalUserPresence(
                                user.availability ?? 'Unknown',
                            ).state,
                            new Date(),
                        ),
                        internalpresence: calculateInternalUserPresence(
                            user.availability ?? 'Unknown',
                        ),
                    })),
                })),
            );
    }
}
